'use client'

import { Icon, Typography } from '@shc/ui'
import distance from '@turf/distance'
import Image from 'next/image'
import isInteger from 'lodash/isInteger'
import NextLink from 'next/link'
import sortBy from 'lodash/sortBy'
import Ratings from '@components/ratings'
import type { Hit } from 'instantsearch.js'
import { dateFirstAvailable } from '@lib/utilities/date-utilities'
import { setSchedulingType } from '@lib/utilities/provider-utilities'
import { useSharedSearchContext } from '@app/[locale]/(main)/search/_contexts/search-context-wrapper'
import { AnalyticsLink } from '@components/analytics'
import createContentfulLoader from '@lib/contentful-image-loader'
import type { GeoData } from '@lib/utilities/location-utilities'
import type { TypeProviderPlaceJoined, TypeProviderPublic } from '@lib/provider-types'
import { useBuildSearchContext } from '@lib/analytics'
import { useInstantSearch } from 'react-instantsearch'
import { type SPContext } from '@hooks/use-analytics'
import { type SendEventForHits } from 'instantsearch.js/es/lib/utils'
import { type SearchNamespace } from '@app/[locale]/(main)/search/_components/search-session'

export type TypeProviderHit = {
  first_available_appt_virtual: number
  first_available_appt_office: number
} & TypeProviderPublic

const getNearestPlace = (
  lat?: number,
  lon?: number,
  places?: TypeProviderPublic['places']
): { place?: TypeProviderPlaceJoined; distanceInMiles?: number } => {
  if (!lat || !lon || !Array.isArray(places)) return {}
  let shortestDistanceFound = Infinity
  const nearestPlaceIndex = places.reduce((result: number, place, index: number) => {
    if (!place.lon || !place.lat) return result
    const dist = distance([lon, lat], [place.lon, place.lat], { units: 'miles' })
    if (dist < shortestDistanceFound) {
      shortestDistanceFound = dist
      return index
    }
    return result
  }, -1)
  return {
    place: places[nearestPlaceIndex],
    distanceInMiles: shortestDistanceFound,
  }
}

const formatDistanceFrom = (provider: TypeProviderPublic & Hit, geoData?: GeoData): string => {
  const { place, distanceInMiles } = getNearestPlace(geoData?.lat, geoData?.lng, provider.places)
  let result = ''
  if (typeof distanceInMiles === 'number' && !isNaN(distanceInMiles)) {
    result += `${distanceInMiles.toFixed(1)} miles away`
  }
  if (place) result += ` · ${place.sublocality ?? place.city}`
  return result
}

const formatFirstAvailable = (epoch: number, type: string) => {
  const zuluString = new Date(epoch).toISOString()
  const apptDate = dateFirstAvailable(zuluString)

  if (type === 'virtual') {
    return `${apptDate} (virtual)`
  } else {
    return `${apptDate} (in person)`
  }
}

// create TypeProviderHit
interface ProviderSearchResultProps {
  hit: Hit<TypeProviderHit>
  url: string
  sendEvent: SendEventForHits
}

const ProviderSearchResult = ({ hit, url, sendEvent }: ProviderSearchResultProps) => {
  const { selectedLocation } = useSharedSearchContext()
  const {
    id,
    photo_url,
    name_formatted_with_degrees,
    first_available_appt_virtual,
    first_available_appt_office,
    patient_panel_status,
    patient_panel_restrictions,
    specialties,
    medical_groups,
    patient_panel_restriction_custom_message,
    is_referable,
    is_providing_virtual_visits,
    name_formatted,
    review_rating,
  } = hit

  const providerPhotoUrl = photo_url?.includes('images.ctfassets.net')
    ? photo_url.replace(/^https?:/i, '')
    : '//images.ctfassets.net/pxcfulgsd9e2/4vtfSBGmp3LQhKtvjw2JIC/d0f2094db56d33c832190f5e281156e5/Placeholder_image.png'

  const schedulingType = setSchedulingType(hit)
  const isVirtualDateNumeric = isInteger(first_available_appt_virtual)
  const isOfficeDateNumeric = isInteger(first_available_appt_office)
  const isAcceptingNewPatients = ['Open', 'Restricted'].includes(patient_panel_status ?? '')
  const namespace: SearchNamespace = 'providers'
  const snowplowSearchContext = useBuildSearchContext()
  const { results } = useInstantSearch()
  const snowplowContexts: SPContext[] = [
    {
      name: 'provider',
      data: {
        provider_id: Number(id ?? -1),
        provider_name: String(name_formatted_with_degrees),
      },
    },
    snowplowSearchContext,
  ]

  const providerNameRatingsBlock = () => {
    return (
      <>
        <Typography variant="h4" as="p">
          <AnalyticsLink
            asPassthru={NextLink}
            href={url}
            noUnderline
            className="hover:underline"
            snowplow={{ contexts: snowplowContexts }}
            algolia={{ click: { namespace, hit, sendEvent } }}>
            {name_formatted_with_degrees}
          </AnalyticsLink>
        </Typography>

        {review_rating && <Ratings value={review_rating} />}
      </>
    )
  }

  const contentfulLoader = createContentfulLoader({
    fm: 'webp',
    fit: 'fill',
    f: 'face',
    q: 80,
    h: 85,
    w: 85,
  })

  const checkPanelRestriction = patient_panel_status === 'Restricted'
  const checkPanelCustom = patient_panel_restrictions?.includes('custom')

  const specialtiesInfo = !!specialties?.length && (
    <div className="flex flex-col gap-2">
      {sortBy(specialties, ['specialty_name']).map((specialty) => {
        return (
          <div key={specialty.specialty_name}>
            <Typography variant="body-semibold">{specialty.specialty_name}</Typography>
          </div>
        )
      })}
    </div>
  )

  const medicalGroupsInfo = !!medical_groups?.length && (
    <div>
      {medical_groups &&
        sortBy(medical_groups, ['name']).map(({ id, name }) => {
          if (name !== 'SharpCare') {
            return (
              <span key={id}>
                {name} Medical Group <br />
              </span>
            )
          }
          return null
        })}
    </div>
  )

  const locationInfoBlock = () => {
    const { aroundLatLngViaIP } = results._state
    const { aroundLatLng } = results

    // if there is no selectedLocation and we are using the default location query (aroundLatLngViaIP=true),
    // create our own geoData object with the lat long we get back from Algolia
    const myGeoData =
      selectedLocation ||
      (aroundLatLngViaIP && aroundLatLng
        ? {
            lat: parseFloat(aroundLatLng.split(',')[0]),
            lng: parseFloat(aroundLatLng.split(',')[1]),
          }
        : null)

    return (
      myGeoData && (
        <Typography className="text-base">
          <Icon
            className="h-4 pr-1.5 items-center justify-center text-primary"
            icon="location-dot"
          />{' '}
          {formatDistanceFrom(hit, myGeoData)}
        </Typography>
      )
    )
  }

  const acceptingNewPatientsMessage = isAcceptingNewPatients ? (
    <div className="text-base">
      <Typography>
        <Icon className="w-3.5 pr-1.5 text-success" icon="check" />
        Accepting new patients
      </Typography>
      {(checkPanelCustom || checkPanelRestriction) && (
        <Typography className="ml-4.5">
          (
          {patient_panel_restriction_custom_message !== null
            ? patient_panel_restriction_custom_message
            : checkPanelRestriction && patient_panel_restrictions}
          )
        </Typography>
      )}
    </div>
  ) : (
    <Typography>
      <Icon className="w-3.5 pr-1.5 text-danger" icon="xmark" />
      Not accepting new patients
    </Typography>
  )

  // TODO:refactor in warranty to use the first available component
  const firstAvailableInfo = is_referable && (
    <div className="flex flex-col gap-4 min-w-[215px] max-w-[310px] flex-grow">
      {schedulingType === 'Online' && (isVirtualDateNumeric || isOfficeDateNumeric) && (
        <div>
          <div className="flex flex-col gap-y-1">
            <Typography variant="body-semibold" as="h5">
              First available
            </Typography>

            {/* Virtual */}
            {isVirtualDateNumeric && (
              <Typography data-testid="first-available-virtual-div">
                <Icon className="w-3.5 pr-1.5" icon="video" />
                {formatFirstAvailable(first_available_appt_virtual, 'virtual')}
              </Typography>
            )}

            {/* In Person */}
            {isOfficeDateNumeric && (
              <Typography data-testid="first-available-person-div">
                <Icon className="w-3.5 pr-1.5" icon="buildings" />
                {formatFirstAvailable(first_available_appt_office, 'in person')}
              </Typography>
            )}
          </div>
        </div>
      )}
      {schedulingType !== 'Online' && (
        <div className="items-start">
          <Typography variant="body-semibold" as="h5">
            Appointments Available
          </Typography>
          <Typography>
            {is_providing_virtual_visits ? 'In-person and virtual visits' : 'In-person'}
          </Typography>
        </div>
      )}
    </div>
  )

  return (
    <div className="flex flex-col md:flex-row gap-4 md:gap-8">
      {/* IMAGE + NAME & RATINGS BLOCK */}
      <div className="flex flex-row md:flex-col gap-4 items-center">
        <AnalyticsLink
          asPassthru={NextLink}
          href={url}
          className="outline-primary outline-2 outline-offset-2 rounded-full"
          snowplow={{ contexts: snowplowContexts }}
          algolia={{ click: { namespace, hit, sendEvent } }}>
          <div className="w-[85px] h-[85px] overflow-hidden rounded-full">
            <Image
              src={providerPhotoUrl}
              aria-label={`${name_formatted_with_degrees} [Image]`}
              alt={photo_url ? `Image of ${name_formatted}` : 'Placeholder image'}
              className="scale-100 hover:scale-105 duration-300 hover:duration-200 ease-in-out"
              width={85}
              height={85}
              loader={contentfulLoader}
            />
          </div>
        </AnalyticsLink>

        {/* Name and ratings (1/2) MOBILE */}
        <div className="flex md:hidden flex-col justify-start items-start">
          {providerNameRatingsBlock()}
        </div>
      </div>

      {/* SPECIALTIES + FIRST AVAILABLE BLOCK */}
      <div className="flex flex-col gap-4 w-full">
        {/* Name and ratings (2/2) DESKTOP */}
        <div className="hidden md:flex flex-row gap-2">{providerNameRatingsBlock()}</div>

        {/* this div wraps the first available when needed */}
        <div className="flex flex-col md:flex-row md:justify-between gap-4">
          {/* specialties + accepting new patients block */}
          <div className="flex flex-col gap-4">
            {/* Specialties */}
            {specialtiesInfo}

            {/* medical groups */}
            {medicalGroupsInfo}

            {is_referable && (
              <div>
                {/* distance away */}
                {locationInfoBlock()}

                {/* accepting new patients */}
                {acceptingNewPatientsMessage}
              </div>
            )}
          </div>

          {/* first available & appointments available */}
          {firstAvailableInfo}
        </div>
      </div>
    </div>
  )
}

export default ProviderSearchResult
